// src/components/module/ventas/CompletarVentaPendiente.tsx
// Rediseño corporativo estilo Notion/Linear
import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  Divider,
  InputAdornment,
  CircularProgress,
  Stack,
  Fade,
  alpha,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  Person as PersonIcon, ReceiptLong, CalendarToday,
  Payment as PaymentIcon, AccountBalanceWallet, CheckCircle,
  AttachMoney, Discount as DiscountIcon, Receipt as ReceiptIcon,
  Savings, ArrowBack, ArrowForward, CreditCard,
} from "@mui/icons-material";
import { VentasApi } from "../../../services/api/VentasApi";
import { formatMoney, formatDate } from "../../../utils/format";
import { VentaTransformada } from "../../../components/types/ventas.types";
import Swal from "sweetalert2";
import { toast } from "../../../utils/toast";
import { useTiposPagoApi } from "../../../hooks/useTiposPagoApi";
import { DarkModeContext } from "../../../context/darkModeContext";

const P = {
  light: {
    bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F",
    textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7", borderH: "#D3D3D0",
    accent: "#2383E2", accentBg: "#E8F0FE", success: "#0F7B6C", successBg: "#DBEDDB",
    warning: "#D9730D", warningBg: "#FADEC9", error: "#E03E3E", errorBg: "#FBE4E4",
    hover: "rgba(55,53,47,0.04)",
  },
  dark: {
    bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6",
    textS: "#9B9A97", textT: "#6B6B6B", border: "#333333", borderH: "#444444",
    accent: "#529CCA", accentBg: "#143A52", success: "#4DAB9A", successBg: "#1A2F2A",
    warning: "#C77D48", warningBg: "#2E2215", error: "#D44C47", errorBg: "#3D2020",
    hover: "rgba(255,255,255,0.04)",
  },
};

const FieldLabel: React.FC<{ label: string; required?: boolean; color: string }> = ({ label, required, color }) => (
  <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color, mb: 0.5, textTransform: "uppercase", letterSpacing: "0.04em" }}>
    {label}{required && <Box component="span" sx={{ color: "#E03E3E", ml: 0.3 }}>*</Box>}
  </Typography>
);

const InfoItem: React.FC<{ icon: React.ReactElement; label: string; value: string; p: any }> = ({ icon, label, value, p }) => (
  <Box>
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.3 }}>
      <Box sx={{ color: p.textT, "& svg": { fontSize: 13 } }}>{icon}</Box>
      <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</Typography>
    </Stack>
    <Typography sx={{ fontSize: "0.88rem", fontWeight: 500, color: p.text }}>{value || "—"}</Typography>
  </Box>
);

const LineItem: React.FC<{ label: string; value: string; icon?: React.ReactElement; color?: string; bold?: boolean; p: any }> = ({ label, value, icon, color, bold, p }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.6 }}>
    <Stack direction="row" alignItems="center" spacing={0.75}>
      {icon && <Box sx={{ color: p.textT, "& svg": { fontSize: 14 } }}>{icon}</Box>}
      <Typography sx={{ fontSize: "0.82rem", color: color || p.textS, fontWeight: bold ? 600 : 400 }}>{label}</Typography>
    </Stack>
    <Typography sx={{ fontSize: bold ? "0.9rem" : "0.84rem", fontWeight: bold ? 700 : 500, color: color || p.text, fontFamily: "'IBM Plex Mono', monospace" }}>{value}</Typography>
  </Stack>
);

interface CompletarVentaPendienteProps {
  numeroFactura: string | null;
}

export const CompletarVentaPendiente: React.FC<CompletarVentaPendienteProps> = ({ numeroFactura }) => {
  const navigate = useNavigate();
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;
  const [ventaInfo, setVentaInfo] = useState<VentaTransformada | null>(null);
  const [tipoPago, setTipoPago] = useState<number>(1);
  const [observaciones, setObservaciones] = useState("");
  const [loading, setLoading] = useState(true);
  const { tiposPago, isLoading: loadingTiposPago } = useTiposPagoApi();
  // Efectivo — monto recibido y cambio
  const [montoRecibido, setMontoRecibido] = useState("");

  const calcularTotalesArticulos = (articulos: any[]) => {
    return articulos.reduce((acc, art) => {
      const sub = art.PrecioVenta * art.Cantidad;
      const desc = sub * (art.Descuento / 100);
      const subD = sub - desc;
      const imp = art.Impuesto?.reduce((a: number, i: any) => a + subD * (i.Valor / 100), 0) || 0;
      return { subtotal: acc.subtotal + sub, descuentoTotal: acc.descuentoTotal + desc, impuestos: acc.impuestos + imp, total: acc.total + subD + imp };
    }, { subtotal: 0, descuentoTotal: 0, impuestos: 0, total: 0 });
  };

  useEffect(() => {
    const cargar = async () => {
      if (!numeroFactura) { navigate("/ventas"); return; }
      try {
        setLoading(true);
        const data = await VentasApi.getVentaById(numeroFactura);
        const tot = calcularTotalesArticulos(data.articulos);
        const subtotal = data.subtotal || tot.subtotal;
        const descuentoTotal = data.descuentoTotal || tot.descuentoTotal;
        const subD = subtotal - descuentoTotal;
        const iva = subD * 0.19;
        const totalAPagar = subD + iva;

        setVentaInfo({
          id: data.id, numeroFactura: data.numeroFactura,
          cliente: { id: parseInt(data.cliente.identificacion), nombre: data.cliente.nombre, identificacion: data.cliente.identificacion },
          fecha: data.fecha, estado: data.estado,
          articulos: data.articulos.map((a: any) => ({ Id: a.Id || 0, Nombre: a.Nombre, EAN: a.EAN || "", PrecioVenta: a.PrecioVenta, Cantidad: a.Cantidad, Descuento: a.Descuento, Valor: a.PrecioVenta * a.Cantidad, stock: a.stock || 0, Impuesto: a.Impuesto || [] })),
          subtotal, descuentoTotal, impuestos: [{ nombre: "IVA", valor: iva }],
          totalAPagar, saldoPendiente: data.saldoPendiente || totalAPagar,
          tipoPago: { id: data.tipoPago?.id || tipoPago, nombre: data.tipoPago?.nombre || "EFECTIVO" },
          observaciones: data.observaciones || "",
        });
        setTipoPago(data.tipoPago?.id || 1);
        setLoading(false);
      } catch { toast.error("No se pudo cargar la venta"); navigate("/ventas"); }
    };
    cargar();
  }, [numeroFactura]);

  const handleProcesar = async () => {
    if (!ventaInfo?.id) return;
    try {
      Swal.fire({ title: "Procesando pago...", allowOutsideClick: false, showConfirmButton: false, didOpen: () => Swal.showLoading() });
      const r = await VentasApi.cobrarFactura({ IdVenta: ventaInfo.id, IdTipoPago: tipoPago, Abono: ventaInfo.saldoPendiente, Observacion: observaciones });
      setMontoRecibido("");
      Swal.close(); toast.success(r.mensaje || "Pago completado");
      navigate("/ventas");
    } catch (e) { Swal.close(); toast.error((e as Error).message); }
  };

  if (loading || loadingTiposPago || !ventaInfo) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={32} sx={{ color: p.accent }} />
      </Box>
  );

  const abonoAnterior = ventaInfo.totalAPagar - ventaInfo.saldoPendiente;
  const pctPagado = ventaInfo.totalAPagar > 0 ? (abonoAnterior / ventaInfo.totalAPagar) * 100 : 0;

  // Cambio para pago en efectivo
  const numMonto = parseFloat(montoRecibido.replace(/[^\d]/g, "") || "0");
  const esEfectivo = tipoPago === 1;
  const cambio = esEfectivo && numMonto > ventaInfo.saldoPendiente
    ? numMonto - ventaInfo.saldoPendiente
    : 0;

  // Formato del input de monto
  const formatInputMoney = (val: string) => {
    const digits = val.replace(/[^\d]/g, "");
    const num = parseInt(digits || "0", 10);
    return num.toLocaleString("es-CO");
  };

  return (
    <Fade in timeout={350}>
        <Box>
          {/* Header */}
          <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: "12px", border: `1px solid ${p.accent}`, bgcolor: p.accentBg }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2.5 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: "10px", bgcolor: p.accent, display: "flex", alignItems: "center", justifyContent: "center", color: "#FFF" }}>
                <ReceiptLong sx={{ fontSize: 22 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: p.text }}>Completar Pago</Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={{ fontSize: "0.82rem", color: p.textS }}>Factura N° {ventaInfo.numeroFactura}</Typography>
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.2, py: 0.3, borderRadius: "6px", bgcolor: p.warningBg, color: p.warning, fontSize: "0.75rem", fontWeight: 600 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: p.warning }} />
                    {ventaInfo.estado}
                  </Box>
                </Stack>
              </Box>
            </Stack>

            {/* Progress bar */}
            <Box>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase" }}>Progreso de pago</Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: p.accent }}>{pctPagado.toFixed(0)}%</Typography>
              </Stack>
              <Box sx={{ width: "100%", height: 6, bgcolor: alpha(p.accent, 0.15), borderRadius: 3, overflow: "hidden" }}>
                <Box sx={{ width: `${pctPagado}%`, height: "100%", bgcolor: p.success, borderRadius: 3, transition: "width 0.4s ease" }} />
              </Box>
            </Box>
          </Paper>

          <Grid container spacing={3}>
            {/* Cliente */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: "12px", border: `1px solid ${p.border}`, bgcolor: p.bg, height: "100%" }}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 650, color: p.text, mb: 2 }}>Cliente</Typography>
                <Stack spacing={2}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ width: 38, height: 38, borderRadius: "8px", bgcolor: p.accentBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.9rem", fontWeight: 700, color: p.accent }}>
                      {ventaInfo.cliente.nombre[0]}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: p.text }}>{ventaInfo.cliente.nombre}</Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: p.textT, fontFamily: "'IBM Plex Mono', monospace" }}>{ventaInfo.cliente.identificacion}</Typography>
                    </Box>
                  </Stack>
                  <Divider sx={{ borderColor: p.border }} />
                  <InfoItem icon={<CalendarToday />} label="Fecha de Venta" value={formatDate(ventaInfo.fecha)} p={p} />
                  <InfoItem icon={<ReceiptIcon />} label="Factura" value={ventaInfo.numeroFactura} p={p} />
                </Stack>
                {ventaInfo.observaciones && (
                  <Box sx={{ mt: 2, p: 2, borderRadius: "8px", bgcolor: p.accentBg, border: `1px solid ${alpha(p.accent, 0.2)}` }}>
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", mb: 0.3 }}>Última observación</Typography>
                    <Typography sx={{ fontSize: "0.82rem", color: p.textS }}>{ventaInfo.observaciones}</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Desglose */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: "12px", border: `1px solid ${p.border}`, bgcolor: p.bg, height: "100%" }}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 650, color: p.text, mb: 2 }}>Desglose</Typography>
                <Stack spacing={0.5}>
                  <LineItem label="Subtotal" value={formatMoney(ventaInfo.subtotal)} icon={<AttachMoney />} p={p} />
                  {ventaInfo.descuentoTotal > 0 && <LineItem label="Descuento" value={`-${formatMoney(ventaInfo.descuentoTotal)}`} icon={<DiscountIcon />} color={p.error} p={p} />}
                  {ventaInfo.impuestos.map((imp, i) => <LineItem key={i} label={imp.nombre} value={formatMoney(imp.valor)} p={p} />)}
                </Stack>
                <Divider sx={{ borderColor: p.border, my: 1.5 }} />

                {/* Total */}
                <Box sx={{ p: 2, borderRadius: "8px", bgcolor: p.bgT, mb: 1.5 }}>
                  <LineItem label="Total Factura" value={formatMoney(ventaInfo.totalAPagar)} bold p={p} />
                </Box>

                {/* Abono */}
                <Box sx={{ p: 2, borderRadius: "8px", bgcolor: p.successBg, mb: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Savings sx={{ fontSize: 14, color: p.success }} />
                      <Typography sx={{ fontSize: "0.82rem", color: p.success, fontWeight: 600 }}>Abono Realizado</Typography>
                    </Stack>
                    <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: p.success, fontFamily: "'IBM Plex Mono', monospace" }}>{formatMoney(abonoAnterior)}</Typography>
                  </Stack>
                </Box>

                {/* Saldo */}
                <Box sx={{ p: 2.5, borderRadius: "10px", border: `1px solid ${p.error}`, bgcolor: p.errorBg }}>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", mb: 0.3 }}>Saldo Pendiente</Typography>
                  <Typography sx={{ fontSize: "1.3rem", fontWeight: 700, color: p.error, fontFamily: "'IBM Plex Mono', monospace" }}>{formatMoney(ventaInfo.saldoPendiente)}</Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Pago */}
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: "12px", border: `1px solid ${p.accent}`, bgcolor: p.bg, height: "100%" }}>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 650, color: p.text, mb: 2 }}>Procesar Pago</Typography>

                <Stack spacing={2.5}>
                  {/* Tipo pago */}
                  <Box>
                    <FieldLabel label="Tipo de Pago" required color={p.textT} />
                    <FormControl fullWidth size="small">
                      <Select value={tipoPago} onChange={(e) => setTipoPago(Number(e.target.value))} displayEmpty
                        sx={{ borderRadius: "8px", bgcolor: p.bgS, fontSize: "0.88rem", "& .MuiOutlinedInput-notchedOutline": { borderColor: p.border }, "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: p.borderH }, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: p.accent } }}>
                        <MenuItem value={0} disabled><Typography sx={{ color: p.textT, fontSize: "0.84rem" }}>Seleccione...</Typography></MenuItem>
                        {tiposPago
                          .filter(t => !/cr[eé]dito/i.test(t.Nombre))
                          .map(t => <MenuItem key={t.Id} value={t.Id}><Stack direction="row" alignItems="center" spacing={1}><CreditCard sx={{ fontSize: 16, color: p.textT }} /><span>{t.Nombre}</span></Stack></MenuItem>)}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Monto recibido (solo efectivo) */}
                  {esEfectivo && (
                    <Box>
                      <FieldLabel label="Monto Recibido" color={p.textT} />
                      <TextField
                        fullWidth
                        value={montoRecibido}
                        onChange={(e) => setMontoRecibido(formatInputMoney(e.target.value))}
                        placeholder={formatMoney(ventaInfo.saldoPendiente)}
                        inputProps={{ inputMode: "numeric" }}
                        sx={{
                          "& .MuiInputBase-root": { borderRadius: "8px", bgcolor: p.bgS, fontSize: "1.1rem", fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", color: p.text },
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: p.border },
                        }}
                      />
                    </Box>
                  )}

                  {/* Saldo / Monto a pagar */}
                  <Box>
                    <FieldLabel label={esEfectivo ? "Saldo Pendiente" : "Monto a Pagar"} color={p.textT} />
                    <Box sx={{ p: 2, borderRadius: "8px", border: `1px solid ${p.success}`, bgcolor: p.successBg }}>
                      <Typography sx={{ fontSize: "1.2rem", fontWeight: 700, color: p.success, fontFamily: "'IBM Plex Mono', monospace", textAlign: "center" }}>
                        {formatMoney(ventaInfo.saldoPendiente)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Cambio */}
                  {esEfectivo && cambio > 0 && (
                    <Box sx={{ p: 2.5, borderRadius: "10px", border: `1px solid ${p.success}`, bgcolor: p.successBg }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Box sx={{ width: 36, height: 36, borderRadius: "8px", bgcolor: p.success, display: "flex", alignItems: "center", justifyContent: "center", color: "#FFF" }}>
                            <AccountBalanceWallet sx={{ fontSize: 18 }} />
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: p.success, textTransform: "uppercase", letterSpacing: "0.04em" }}>Cambio a devolver</Typography>
                            <Typography sx={{ fontSize: "0.72rem", color: p.textS }}>Recibido: {formatMoney(numMonto)}</Typography>
                          </Box>
                        </Stack>
                        <Typography sx={{ fontSize: "1.3rem", fontWeight: 700, color: p.success, fontFamily: "'IBM Plex Mono', monospace" }}>
                          {formatMoney(cambio)}
                        </Typography>
                      </Stack>
                    </Box>
                  )}

                  {/* Observaciones */}
                  <Box>
                    <FieldLabel label="Observaciones" color={p.textT} />
                    <TextField fullWidth size="small" multiline rows={2} value={observaciones} onChange={(e) => setObservaciones(e.target.value)}
                      placeholder="Notas sobre este pago..."
                      sx={{ "& .MuiInputBase-root": { borderRadius: "8px", bgcolor: p.bgS, fontSize: "0.84rem" }, "& .MuiOutlinedInput-notchedOutline": { borderColor: p.border } }}
                    />
                  </Box>

                  {/* Botones */}
                  <Stack spacing={1.5} sx={{ mt: 1 }}>
                    <Button variant="contained" fullWidth disableElevation
                      startIcon={<CheckCircle sx={{ fontSize: 16 }} />}
                      endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
                      onClick={handleProcesar}
                      disabled={!ventaInfo.id || ventaInfo.saldoPendiente <= 0 || tipoPago === 0}
                      sx={{
                        bgcolor: p.success, color: "#FFF", py: 1.3, fontSize: "0.88rem", fontWeight: 700, borderRadius: "8px",
                        "&:hover": { bgcolor: darkMode ? "#5DC0AD" : "#0A6B5E" },
                        "&.Mui-disabled": { bgcolor: alpha(p.success, 0.15), color: alpha(p.text, 0.3) },
                      }}>
                      Procesar Pago
                    </Button>
                    <Button fullWidth onClick={() => navigate("/ventas")}
                      startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
                      sx={{ color: p.textS, fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px", "&:hover": { bgcolor: p.hover } }}>
                      Volver a Ventas
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Fade>
  );
};
